import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  deleteMemberTypeChangeRequest,
  fetchMembershipPlanUpgradationRequest,
  handleSendMembershipTypeChangeOtp,
  handleVerifyMembershipTypeChangeOtpRequest,
  resourceCountriesList,
} from 'store/slices';

import usePermission from "hooks/usePermission";

import {
  paymentColor,
  getDataFromLocalStorage,
  getUserType,
} from 'utils/helpers';
import { objectToFormData } from 'utils/helpers';

import { icons, tabs } from 'utils/constants';

import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';
import Button from 'components/form/Button';
import TabLayout from 'components/Layout/TabLayout';
import SeachInput from 'components/form/SeachInput';
import DeletePopup from 'components/Layout/DeletePopup';
import ExportButton from 'components/Layout/ExportButton';

import './MembershipTypeChange.scss';
import BulkAction from '../Common/BulkAction';

const MembershipTypeChange = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userType = getDataFromLocalStorage('user_type');
  const { viewAccess, deleteAccess } = usePermission("Requests");

  const [childTabType, setChildTabType] = useState('0');
  const [isDeleteRequestId, setIsDeleteRequestId] = useState('');
  const { membershipList } = useSelector(
    (state) => ({
      membershipList: state.global.membershipList,
    })
  );
  
  const urlType = ['0', '1'].includes(userType)
    ? 'request-received'
    : 'raised-request';

  const [searchText, setSearchText] = useState('');
  const [timer, setTimer] = useState('');
  const [searchPayload, setSearchPayload] = useState({
    name: '',
    profile_completion: '',
    current_member_type: '',
    switch_to_member_type: '',
    phone_number: '',
    country: '',
    join_date: '',
    valid_date: '',
    status: '',
  });

  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: 10,
    event_by_filter: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [tableList, setTableList] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);
  const [isCheckAll, setIsCheckAll] = useState(false);

  const fetchResourceCountry = async () => {
    await dispatch(resourceCountriesList());
  };

  useEffect(() => {
    if (userType === '6') fetchResourceCountry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const getMembershipPlanUpgradationRequest = async (obj) => {
    setIsLoading(true);
    setSelectedIds([])
    setTableList([])
    setFilterData((prev) => ({
      ...prev,
      total: 0,
    }));
    let payload = objectToFormData({ ...obj, tab_status: childTabType });
    const response = await dispatch(
      fetchMembershipPlanUpgradationRequest(payload)
    );
    setTableList(response?.data?.membership_request || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setIsLoading(false);
  };

  // Fetch membership plan upgradation requests on component mount
  useEffect(() => {
    if (['0', '1', '6'].includes(userType)) {
      getMembershipPlanUpgradationRequest({ ...filterData, ...searchPayload });
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childTabType]);

  const handleSearch = (e) => {
    let value = e?.target?.value;
    setSearchText(value);
    let time = timer;
    clearTimeout(time);
    time = setTimeout(() => {
      const updatedSearchPayload = { ...searchPayload, global_filter: value };
      setSearchPayload(updatedSearchPayload);
      const updatedFilterData = {
        ...filterData,
        offset: 0,
        ...updatedSearchPayload,
      };
      setIsLoading(true);
      setFilterData(updatedFilterData);
      getMembershipPlanUpgradationRequest(updatedFilterData);
    }, 800);
    setTimer(time);
  };

  const handelChangeSearch = (searchData) => {
    setIsLoading(true);
    let newData = filterData;
    setSearchPayload(searchData);
    newData = { ...newData, ...searchData, offset: 0 };
    setFilterData(newData);
    getMembershipPlanUpgradationRequest(newData);
  };

  const handelChangePagination = (offset, newLimit) => {
    setIsLoading(true);
    const updatedData = {
      ...filterData,
      ...searchPayload,
      offset,
      limit: newLimit ?? filterData.limit,
    };
    setFilterData(updatedData);
    getMembershipPlanUpgradationRequest(updatedData);
  };

  //BulkApproved
  const handleSelectAllChange = () => {
    const newChecked = !isCheckAll;
    setIsCheckAll(newChecked);
    setSelectedIds(newChecked ? tableList.map(({ request_id }) => request_id) : []);
  };

  const handleCheckboxChange = (id, checked) => {
    setSelectedIds((prev) => {
      const updated = checked ? [...prev, id] : prev.filter((x) => x !== id);
      setIsCheckAll(updated.length === tableList.length);
      return updated;
    });
  };


  const header = [
    ...(childTabType === '0' && ['1'].includes(userType) ? [{ isSearch: false }] : [{
      isSearch: false,
      title: 'S.R No.',
    },
    ]),
    {
      isSearch: true,
      searchInputName: 'name',
      title: 'Name',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          { value: 'Above 80%', name: 'Above 80' },
          { value: '50 - 79%', name: '50 - 79' },
          { value: 'Below 50%', name: 'Below 50' },
        ],
        key: 'name',
        value: 'value',
      },
      searchInputName: 'profile_completion',
      title: <div className="text-nowrap">Profile Completion</div>,
    },

    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: membershipList,
        key: 'name',
        value: 'id',
      },
      searchInputName: 'current_type',
      title: 'Current Type',
    },
    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: membershipList,
        key: 'name',
        value: 'id',
      },
      searchInputName: 'switch_to',
      title: 'Switch To',
    },
    {
      isSearch: true,
      searchInputName: 'phone_number',
      title: 'Phone Number',
    },
    {
      isSearch: true,

      searchInputName: 'country',
      title: <div className="text-nowrap">Country</div>,
    },

    {
      isSearch: true,
      isDatePicker: true,
      searchInputName: 'join_date',
      title: 'Join Date',
    },
    {
      isSearch: true,
      isDatePicker: true,
      searchInputName: 'valid_till',
      title: 'Valid Till',
    },

    {
      isSearch: true,
      isSearchDropdown: true,
      dropdownOptions: {
        options: [
          { name: 'Pending' },
          { name: 'In-Progress' },
          { name: 'Approved' },
          { name: 'Rejected' },
        ],
        key: 'name',
        value: 'name',
      },
      searchInputName: 'request_status',
      title: 'Request Status',
    },
    {
      isSearch: false,
      // searchLable: 'Edit/Delete',
      title: 'Action',
    },
  ];

  const rowData = [];
  tableList?.forEach((elem, index) => {
    const {
      name,
      current_member_type,
      switch_to_member_type,
      phone_number,
      country,
      status,
      memberType,
      join_date,
      valid_till,
      profile_completion,
      request_id,
      delete_status,
    } = elem;
    let obj = [
      ...(childTabType === '0' && ['1'].includes(userType)
        ? [
          {
            value: (
              <div className="d-flex justify-content-center align-items-center">
                <input
                  type="checkbox"
                  className={`custom-checkbox ${selectedIds.includes(elem?.request_id)
                    ? 'activeChecked'
                    : 'activeUnchecked'
                    }`}
                  onChange={(e) =>
                    handleCheckboxChange(elem?.request_id, e.target.checked)
                  }
                  checked={selectedIds.includes(elem?.request_id)}
                />
              </div>
            ),
          },
        ]
        : [{
          value: index + 1
        }]),
      {
        value: <div className="pointer color-new-car">{name || '-'}</div>,
      },
      {
        value: (
          <span>
            {profile_completion
              ? profile_completion === '0'
                ? '0'
                : `${profile_completion}%`
              : '-'}
          </span>
        ),
      },
      {
        value: (
          <span className="text-capitalize">{current_member_type || '-'}</span>
        ),
      },
      {
        value: (
          <span className="text-capitalize">
            {switch_to_member_type || '-'}
          </span>
        ),
      },
      {
        value: phone_number || '-',
      },
      {
        value: country || '-',
      },
      {
        value: join_date || '-',
      },
      {
        value: valid_till || '-',
      },
      {
        value: <div className={paymentColor(status)}>{status || '-'}</div>,
      },
      {
        value: (
          <span className="action-icon-buttons">
            {viewAccess === '1' && (
              <Button
                btnStyle="light-outline"
                icon={<i className="bi bi-eye" />}
                className="me-2"
                onClick={() => {
                  navigate(
                    `/${getUserType()}/${urlType}/membership-type-change/${request_id}`
                  );
                }}
                isSquare
              />
            )}
            {delete_status === '0' && deleteAccess === '1' && (
              <Button
                btnStyle="light-outline"
                icon={<img src={icons.deleteIcon} alt="delete" />}
                onClick={() => {
                  setIsDeleteRequestId(request_id);
                }}
                isSquare
              />
            )}
          </span>
        ),
      },
    ];

    rowData.push({ data: obj });
  });

  return (
    <div id="membershiptypechange-container">
      {isDeleteRequestId && (
        <DeletePopup
          title="Delete Membership Type Change Request"
          message="Are you sure you want to delete this Request?"
          onHide={() => {
            setIsDeleteRequestId('');
          }}
          handelSuccess={() => {
            getMembershipPlanUpgradationRequest({
              ...filterData,
              ...searchPayload,
            });
            setIsDeleteRequestId('');
          }}
          handelDelete={async () => {
            let payload = {
              request_id: isDeleteRequestId,
            };
            let forData = objectToFormData(payload);
            const response = await dispatch(
              deleteMemberTypeChangeRequest(forData)
            );
            return response;
          }}
        />
      )}

      <Card className="cps-20 cpe-20 cpb-20">
        <div className="cpt-14">
          <TabLayout
            tabs={tabs}
            activeKey={childTabType}
            onChange={setChildTabType}
          />
        </div>

        {selectedIds.length === 0 ? (
          <div className="d-flex justify-content-between align-items-center cpt-20 cpb-20">
            <div className="table-title">
              Membership Type Change Request ({filterData?.total})
            </div>

            <div className="d-flex">
              <div style={{ width: '150px' }} >
                <SeachInput
                  placeholder="Search"
                  value={searchText}
                  onChange={handleSearch}
                  className="small-text-input"
                  iconClassName="small-search-icon"
                />
              </div>

              <div className="me-3">
                <ExportButton
                  exportAPI={fetchMembershipPlanUpgradationRequest}
                  payload={objectToFormData({
                    ...filterData,
                    ...searchPayload,
                    export_status: 1,
                  })}
                />
              </div>
            </div>
          </div>) : childTabType === "0" && (
            <BulkAction
              isCheckAll={isCheckAll}
              handleSelectAllChange={handleSelectAllChange}
              isLoading={isLoading}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              handleSendOtp={handleSendMembershipTypeChangeOtp}
              handleVerifyOtpRequest={handleVerifyMembershipTypeChangeOtpRequest}
              typeTitle="Membership Type Change"
              onSuccess={() => getMembershipPlanUpgradationRequest({ ...filterData, ...searchPayload })}
            />
          )}
        <Table
          isLoading={isLoading}
          header={header}
          rowData={rowData}
          filterData={filterData}
          searchPayload={searchPayload}
          searchInputChange={handelChangeSearch}
          changeOffset={handelChangePagination}
          maxHeight="60vh"
          showRowsLimit={['0', '1'].includes(userType)}
        />
      </Card>
    </div>
  );
};

export default MembershipTypeChange;
